import { useMemo, useState, type ReactElement } from 'react'
import { getTeamFullName, useProfile, useTeams } from '@front/model'
import { useGroupedTeamOptions } from '@hooks'
import { CloseButton, Combobox, Input, InputBase, useCombobox, type ComboboxProps, Group, Text } from '@mantine/core'
import { isDefined, isEmpty, isNotEmpty, isTeamId, type CompanyId, type TeamData, type TeamId } from '@terros/common'
import { PrefetchedTeamInfo } from 'app/components/entityInfo'
import { MaterialSymbolIcon } from 'app/components/terros'
import { useTeamComboboxOptions } from './TeamComboboxOptions'

type TeamMultiSelectProps = {
  value: TeamId[]
  onChange: (value: TeamId[]) => void
  teamFilter?: (team: TeamData) => boolean
  companyId?: CompanyId
  label?: string
  width?: ComboboxProps['width']
  placeholder?: string
  allowDeselect?: boolean
  handleSelectAll?: (teamIds: TeamId[]) => void
}

export const TeamMultiSelect = ({
  teamFilter,
  companyId,
  label,
  width,
  value,
  onChange,
  placeholder = 'Select teams',
  allowDeselect = true,
  handleSelectAll,
}: TeamMultiSelectProps): ReactElement => {
  const [query, setQuery] = useState('')
  const { user, company } = useProfile()
  const { data: teams } = useTeams({ companyId: companyId ?? user?.companyId })
  const availableTeams = useMemo(() => {
    if (!user) return []
    if (isEmpty(teams)) return []
    if (isDefined(teamFilter)) {
      return teams.filter(teamFilter)
    }
    return teams
  }, [teams, teamFilter, user])

  const groupedTeamOptions = useGroupedTeamOptions(availableTeams, company)

  const combobox = useCombobox({})

  const selectedTeams = useMemo(() => {
    return availableTeams.filter((team) => value.includes(team.teamId))
  }, [availableTeams, value])

  const allSelected = useMemo(() => {
    return isNotEmpty(availableTeams) && value.length === availableTeams.length
  }, [availableTeams, value])

  const handleToggleTeam = (teamId: TeamId): void => {
    if (value.includes(teamId)) {
      onChange(value.filter((id) => id !== teamId))
    } else {
      onChange([...value, teamId])
    }
  }

  const displayValue = useMemo(() => {
    if (isEmpty(selectedTeams)) {
      return null
    }

    if (selectedTeams.length === 1) {
      return <PrefetchedTeamInfo linked={false} team={selectedTeams[0]} />
    }

    return selectedTeams.map((team) => getTeamFullName(company, team)).join(', ')
  }, [selectedTeams, company])

  const options = useTeamComboboxOptions({
    groupedTeamOptions,
    availableTeams,
    selectedValue: value,
  })

  return (
    <Combobox
      store={combobox}
      width={width ?? 350}
      onOptionSubmit={(teamId) => {
        if (teamId === 'select-all') {
          if (handleSelectAll) {
            handleSelectAll(availableTeams.map((team) => team.teamId))
          }
          return
        }
        if (isTeamId(teamId)) {
          handleToggleTeam(teamId)
          return
        }
      }}
    >
      <Combobox.Target>
        <InputBase
          label={label}
          pointer
          type='button'
          component='button'
          rightSection={
            allowDeselect && isNotEmpty(selectedTeams) ? (
              <CloseButton
                size='sm'
                onClick={(e) => {
                  e.preventDefault()
                  onChange([])
                }}
                aria-label='Clear value'
              />
            ) : (
              <Combobox.Chevron />
            )
          }
          rightSectionPointerEvents={allowDeselect && isNotEmpty(selectedTeams) ? 'all' : 'none'}
          onClick={() => combobox.toggleDropdown()}
        >
          {displayValue ? displayValue : <Input.Placeholder>{placeholder}</Input.Placeholder>}
        </InputBase>
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Search
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          placeholder='Search teams'
        />
        <Combobox.Options mah={400} style={{ overflowY: 'auto' }}>
          {isNotEmpty(availableTeams) && isDefined(handleSelectAll) && (
            <Combobox.Option value='select-all'>
              <Group justify='space-between'>
                <Text>Select All</Text>
                {allSelected && <MaterialSymbolIcon icon='check' size='lg' />}
              </Group>
            </Combobox.Option>
          )}
          {options}
          {isEmpty(options) && <Combobox.Empty>No teams found</Combobox.Empty>}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
